import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import soundboardCatalog from '../lib/generatedSoundboardCatalog.json';
import { getLocalizedSoundName, getLocalizedCategoryName } from '../lib/soundboardTranslations';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const STORAGE_KEY = 'wyrdmaker.soundboard.settings.v1';
const DEFAULT_MASTER_VOLUME = 0.8;
const DEFAULT_SOUND_VOLUME = 0.7;

// ─────────────────────────────────────────────────────────────────────────────
// Storage helpers
// ─────────────────────────────────────────────────────────────────────────────
function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function saveSettings(settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // ignore
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Search helpers
// ─────────────────────────────────────────────────────────────────────────────
function normalizeStr(str) {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

// ─────────────────────────────────────────────────────────────────────────────
// useSoundboard hook
// ─────────────────────────────────────────────────────────────────────────────
export function useSoundboard(lang = 'fr') {
  // ── Catalog (with localized display names) ─────────────────────────────
  const categories = useMemo(() => {
    return (soundboardCatalog.categories || []).map(cat => ({
      ...cat,
      displayName: getLocalizedCategoryName(cat.name, lang),
      sounds: cat.sounds.map(s => ({
        ...s,
        displayName: getLocalizedSoundName(s.name, lang),
      })),
    }));
  }, [lang]);

  const allSounds = useMemo(() => {
    const sounds = [];
    for (const cat of categories) {
      for (const sound of cat.sounds) {
        sounds.push({ ...sound, categoryId: cat.id, categoryName: cat.displayName });
      }
    }
    return sounds;
  }, [categories]);

  const soundsById = useMemo(() => {
    const map = new Map();
    for (const s of allSounds) {
      map.set(s.id, s);
    }
    return map;
  }, [allSounds]);

  // ── State ────────────────────────────────────────────────────────────────
  const saved = useMemo(() => loadSettings(), []);

  const [masterVolume, setMasterVolumeState] = useState(
    saved.masterVolume ?? DEFAULT_MASTER_VOLUME,
  );
  const [soundVolumes, setSoundVolumes] = useState(saved.soundVolumes || {});
  const [activeSoundIds, setActiveSoundIds] = useState(new Set());
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    categories[0]?.id || null,
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [libraryOpen, setLibraryOpen] = useState(false);

  // ── Audio instances (imperative) ─────────────────────────────────────────
  const audioRefs = useRef(new Map()); // soundId -> HTMLAudioElement

  // ── Persist settings ─────────────────────────────────────────────────────
  useEffect(() => {
    saveSettings({ masterVolume, soundVolumes });
  }, [masterVolume, soundVolumes]);

  // ── Master volume sync ───────────────────────────────────────────────────
  const masterVolumeRef = useRef(masterVolume);
  masterVolumeRef.current = masterVolume;

  const soundVolumesRef = useRef(soundVolumes);
  soundVolumesRef.current = soundVolumes;

  const setMasterVolume = useCallback((vol) => {
    const v = Math.max(0, Math.min(1, vol));
    setMasterVolumeState(v);
    // Update all active sounds
    audioRefs.current.forEach((audio, soundId) => {
      const sv = soundVolumesRef.current[soundId] ?? DEFAULT_SOUND_VOLUME;
      audio.volume = sv * v;
    });
  }, []);

  // ── Computed volume ──────────────────────────────────────────────────────
  const getEffectiveVolume = useCallback(
    (soundId) => {
      const sv = soundVolumes[soundId] ?? DEFAULT_SOUND_VOLUME;
      return sv * masterVolume;
    },
    [soundVolumes, masterVolume],
  );

  // ── Play a sound ─────────────────────────────────────────────────────────
  const playSound = useCallback(
    (soundId) => {
      const sound = soundsById.get(soundId);
      if (!sound) return;

      // If already active, restart it
      const existing = audioRefs.current.get(soundId);
      if (existing) {
        existing.currentTime = 0;
        existing.play().catch(() => {});
        setActiveSoundIds((prev) => new Set(prev).add(soundId));
        return;
      }

      const audio = new Audio(sound.src);
      audio.loop = sound.isLoop;
      audio.volume =
        (soundVolumesRef.current[soundId] ?? DEFAULT_SOUND_VOLUME) *
        masterVolumeRef.current;
      audio.preload = 'auto';

      audio.addEventListener('ended', () => {
        if (!sound.isLoop) {
          audioRefs.current.delete(soundId);
          setActiveSoundIds((prev) => {
            const next = new Set(prev);
            next.delete(soundId);
            return next;
          });
        }
      });

      audio.addEventListener('error', () => {
        audioRefs.current.delete(soundId);
        setActiveSoundIds((prev) => {
          const next = new Set(prev);
          next.delete(soundId);
          return next;
        });
      });

      audioRefs.current.set(soundId, audio);
      setActiveSoundIds((prev) => new Set(prev).add(soundId));
      audio.play().catch(() => {});
    },
    [soundsById],
  );

  // ── Toggle a sound (play if stopped, stop if playing) ────────────────────
  const toggleSound = useCallback(
    (soundId) => {
      if (activeSoundIds.has(soundId)) {
        // Stop it
        const audio = audioRefs.current.get(soundId);
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
        audioRefs.current.delete(soundId);
        setActiveSoundIds((prev) => {
          const next = new Set(prev);
          next.delete(soundId);
          return next;
        });
      } else {
        playSound(soundId);
      }
    },
    [activeSoundIds, playSound],
  );

  // ── Stop a sound ─────────────────────────────────────────────────────────
  const stopSound = useCallback((soundId) => {
    const audio = audioRefs.current.get(soundId);
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    audioRefs.current.delete(soundId);
    setActiveSoundIds((prev) => {
      const next = new Set(prev);
      next.delete(soundId);
      return next;
    });
  }, []);

  // ── Stop all ─────────────────────────────────────────────────────────────
  const stopAll = useCallback(() => {
    audioRefs.current.forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
    });
    audioRefs.current.clear();
    setActiveSoundIds(new Set());
  }, []);

  // ── Set volume for individual sound ──────────────────────────────────────
  const setSoundVolume = useCallback(
    (soundId, vol) => {
      const v = Math.max(0, Math.min(1, vol));
      setSoundVolumes((prev) => ({ ...prev, [soundId]: v }));

      const audio = audioRefs.current.get(soundId);
      if (audio) {
        audio.volume = v * masterVolumeRef.current;
      }
    },
    [],
  );

  // ── Filtered sounds for library ──────────────────────────────────────────
  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCategoryId) || null,
    [categories, selectedCategoryId],
  );

  const filteredSounds = useMemo(() => {
    let sounds = selectedCategory ? selectedCategory.sounds : allSounds;

    if (searchTerm.trim()) {
      const q = normalizeStr(searchTerm.trim());
      sounds = sounds.filter(
        (s) =>
          normalizeStr(s.name).includes(q) ||
          (s.displayName && normalizeStr(s.displayName).includes(q)) ||
          normalizeStr(s.filename).includes(q),
      );
    }

    return sounds.map((s) => ({
      ...s,
      categoryId: s.categoryId || selectedCategory?.id,
      categoryName: s.categoryName || selectedCategory?.displayName,
    }));
  }, [selectedCategory, allSounds, searchTerm]);

  // ── Active sounds list for mixer ─────────────────────────────────────────
  const activeSounds = useMemo(() => {
    return Array.from(activeSoundIds)
      .map((id) => soundsById.get(id))
      .filter(Boolean);
  }, [activeSoundIds, soundsById]);

  // ── Cleanup on unmount ───────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      audioRefs.current.forEach((audio) => {
        audio.pause();
        audio.currentTime = 0;
      });
      audioRefs.current.clear();
    };
  }, []);

  return {
    // Catalog data
    categories,
    allSounds,
    soundsById,

    // Current state
    activeSounds,
    activeSoundIds,
    masterVolume,
    soundVolumes,
    selectedCategoryId,
    selectedCategory,
    searchTerm,
    filteredSounds,
    libraryOpen,

    // Actions
    playSound,
    toggleSound,
    stopSound,
    stopAll,
    setMasterVolume,
    setSoundVolume,
    getEffectiveVolume,
    setSelectedCategoryId,
    setSearchTerm,
    setLibraryOpen,

    // Constants
    DEFAULT_SOUND_VOLUME,
  };
}

export default useSoundboard;
